import type { AdminTestimonial } from "@/lib/admin-api-types";
import type { Testimonial } from "@/lib/api-types";
import { withTestimonialFallbacks } from "@/lib/site-content-fallbacks";

/** Cards visible on home at once. */
export const HOME_TESTIMONIALS_VISIBLE_COUNT = 3;

/** Max published testimonials in the home rotation pool. */
export const HOME_TESTIMONIALS_SLIDESHOW_CAP = 12;

export function isTestimonialPublished(
  t: AdminTestimonial,
  now: Date = new Date()
): boolean {
  if (t.published === false) return false;
  if (!t.publishAt) return true;
  const at = Date.parse(t.publishAt);
  if (Number.isNaN(at)) return false;
  return at <= now.getTime();
}

export function adminTestimonialToPublic(t: AdminTestimonial): Testimonial {
  return withTestimonialFallbacks([
    {
      id: t.id,
      quote: t.quote,
      name: t.name,
      role: t.role,
      image: t.image,
    },
  ])[0];
}

/** Ascending sortOrder; newer items first when order ties. */
export function sortTestimonialsForDisplay(
  items: AdminTestimonial[]
): AdminTestimonial[] {
  return [...items].sort((a, b) => {
    const order = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (order !== 0) return order;
    const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
    const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
    return bTime - aTime;
  });
}

/** Published admin testimonials for home rotator / preview. */
export function selectPublishedHomeTestimonials(
  items: AdminTestimonial[],
  limit = HOME_TESTIMONIALS_SLIDESHOW_CAP
): Testimonial[] {
  return sortTestimonialsForDisplay(items.filter((t) => isTestimonialPublished(t)))
    .slice(0, limit)
    .map(adminTestimonialToPublic);
}

/** Public API list is pre-sorted; take up to pool cap for home rotator. */
export function selectPublicHomeTestimonials(
  items: Testimonial[],
  limit = HOME_TESTIMONIALS_SLIDESHOW_CAP
): Testimonial[] {
  return items.slice(0, limit);
}

/** Visible slice for the home rotator (sequential window over the pool). */
export function getTestimonialWindow(
  items: Testimonial[],
  offset: number,
  visibleCount = HOME_TESTIMONIALS_VISIBLE_COUNT
): Testimonial[] {
  if (items.length === 0) return [];
  if (items.length <= visibleCount) return items;

  const start = ((offset % items.length) + items.length) % items.length;
  return Array.from({ length: visibleCount }, (_, i) => items[(start + i) % items.length]);
}

/** Shift existing items down so a new testimonial can take sortOrder 0 (home first). */
export function bumpTestimonialSortOrders(items: AdminTestimonial[]): void {
  for (const t of items) {
    t.sortOrder = (t.sortOrder ?? 0) + 1;
  }
}
